import { useState } from 'react';
import FullHeight from 'components/FullHeight';
import Breadcrumbs from 'components/Breadcrumbs';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Container } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  useAddOrganisationToGroup,
  useGetGroup,
  useRemoveOrganisationFromGroup,
} from 'api';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import FullScreenSpinner from 'components/FullScreen/FullScreenSpinner';
import FullScreenError from 'components/FullScreen/FullScreenError';
import {
  AddRemoveModal,
  Chip,
  TextV2,
  SvgAddRegular,
  ButtonV2,
  Alert,
  Dialog,
} from '@insights-ltd/design-library/components';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { GroupType, OrganisationResponse } from 'api/httpEntities';
import { useGetAllOrganisationsAndGroups } from 'components/hooks/useGetAllOrganisationsAndGroups';
import { useQueryClient } from '@tanstack/react-query';
import PermissionGuard from 'components/PermissionGuard';
import { useAuth } from 'contexts/AuthContext';
import { parseDurationFromIsoString } from '@insights-ltd/design-library/utils';
import {
  DataProtectionDifference,
  getDataProtectionDifference,
} from 'utils/dates';
import { ActionDropdown } from 'components/ActionDropdown/ActionDropdown';
import GroupOrganisationList from './GroupOrganisationList';
import {
  GroupOrganisationError,
  errorHandler,
  useGetGroupOrganisationErrorMessage,
} from './Group.helper';

const Group: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { groupId } = useParams<{ groupId: string }>();
  const successfulRoleChange =
    null !== useSearchParams()[0].get('successfulRoleChange');
  const [groupOrganisationError, setGroupOrganisationError] =
    useState<GroupOrganisationError>('OTHER');
  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
  const [organisationToAdd, setOrganisationToAdd] = useState<
    OrganisationResponse | undefined
  >(undefined);
  const errorMsg = useGetGroupOrganisationErrorMessage(
    groupOrganisationError,
    organisationToAdd?.name || '',
  );
  const { user } = useAuth();
  const { data: group, status, refetch } = useGetGroup(groupId || '');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const {
    availableOrganisations,
    organisations,
    refetchData: refetchGroups,
  } = useGetAllOrganisationsAndGroups();

  const { mutate: mutateGroupToAddOrganisation } =
    useAddOrganisationToGroup(queryClient);
  const { mutate: removeOrganisationToGroup } =
    useRemoveOrganisationFromGroup(queryClient);

  const close = () => {
    setIsOpen(false);
    setGroupOrganisationError('OTHER');
  };
  const [
    openDataRetentionConfirmationModal,
    setOpenDataRetentionConfirmationModal,
  ] = useState<boolean>(false);

  const refetchData = () => {
    refetchGroups();
    refetch().then();
  };

  const groupTypeLabel: Record<GroupType, string> = {
    CUSTOMER: t('ui.event-management.organisations.group.label.customer'),
    PARTNER: t('ui.event-management.organisations.group.label.partner'),
  };

  const breadcrumbText: Record<GroupType, string> = {
    CUSTOMER: t('ui.event-management.group.breadcrumb.customer', {
      groupName: group?.name,
    }),
    PARTNER: t('ui.event-management.group.breadcrumb.partner', {
      groupName: group?.name,
    }),
  };

  if (status === 'pending') {
    return <FullScreenSpinner message={t('ui.event-management.loading')} />;
  }
  if (status === 'error') {
    return (
      <FullScreenError
        message={t('ui.event-management.organisation.error-loading')}
      />
    );
  }

  const translations = {
    addAllLoading: '',
    title: t('ui.event-management.group.add-remove-organisation.dialog.title', {
      groupName: group.name,
    }),
    clear: t('ui.event-management.team.add-remove-modal.clear'),
    findItem: t(
      'ui.event-management.group.add-remove-organisation.dialog.find-organisations',
    ),
    findItemPlaceHolder: t(
      'ui.event-management.group.add-remove-organisation.dialog.find-organisations.placeholder',
    ),
    findItemResults: {
      prompt: t(
        'ui.event-management.group.add-remove-organisation.dialog.find-organisations.search-results.prompt',
      ),
      plural: (count: number) =>
        t(
          'ui.event-management.group.add-remove-organisation.dialog.find-organisations.search-results.plural',
          { count },
        ),
      singular: (count: number) =>
        t(
          'ui.event-management.group.add-remove-organisation.dialog.find-organisations.search-results.singular',
          { count },
        ),
      none: () =>
        t(
          'ui.event-management.group.add-remove-organisation.dialog.find-organisations.search-results.none',
        ),
    },
    itemsInGroup: t(
      'ui.event-management.group.add-remove-organisation.dialog.organisations-in-group',
      { count: group.organisations.length },
    ),
    itemsInGroupPlaceholder: t(
      'ui.event-management.group.add-remove-organisation.dialog.organisations-in-group.placeholder',
    ),
    itemsInGroupResults: {
      plural: (count: number) =>
        t(
          'ui.event-management.group.add-remove-organisation.dialog.organisations-in-group.search-results.plural',
          { count },
        ),
      singular: (count: number) =>
        t(
          'ui.event-management.group.add-remove-organisation.dialog.organisations-in-group.search-results.singular',
          { count },
        ),
      none: () =>
        t(
          'ui.event-management.group.add-remove-organisation.dialog.organisations-in-group.search-results.none',
        ),
    },
    add: t('ui.event-management.group.add-remove-organisation.dialog.add'),
    remove: t(
      'ui.event-management.group.add-remove-organisation.dialog.remove',
    ),
    finish: t(
      'ui.event-management.group.add-remove-organisation.dialog.finish',
    ),
    errorMsg,
    addAll: {
      addAllText: t('ui.event-management.group.add-remove.add-all', {
        count: availableOrganisations.length,
      }),
      confirmAddAllAction: t(
        'ui.event-management.group.add-remove.add-all.confirm',
      ),
      cancelAddAllAction: t(
        'ui.event-management.group.add-remove.add-all.cancel',
      ),
      confirmationTitle: t(
        'ui.event.management.team.add-remove-learners.dialog.add-all-confirm',
      ),
    },
    dataRetentionModal: {
      header: t(
        'ui.event-management.group.add-remove-organisation.confirm.data-retention-period-difference.modal.header',
      ),
      body: t(
        'ui.event-management.group.add-remove-organisation.confirm.data-retention-period-difference.modal.body',
      ),
      areYouSure: t(
        'ui.event-management.group.add-remove-organisation.confirm.data-retention-period-difference.modal.are-you-sure',
      ),
      alert: t(
        'ui.event-management.group.add-remove-organisation.confirm.data-retention-period-difference.modal.alert',
      ),
      buttonYes: t(
        'ui.event-management.group.add-remove-organisation.confirm.data-retention-period-difference.modal.button.yes',
      ),
      buttonNo: t(
        'ui.event-management.group.add-remove-organisation.confirm.data-retention-period-difference.modal.button.no',
      ),
    },
  };

  const actionItems = [
    {
      text: t('ui.event-management.group.manage-group'),
      value: `/groups/${group?.id}/edit`,
    },

    {
      text: t('ui.event-management.bulk-set-user-permissions.action'),
      value: `/organisations/set-bulk-permissions?groupId=${group?.id}`,
    },
  ];

  const dataRetentionPeriod =
    group.dataRetentionPolicy?.maximumInactivityPeriod;

  const addOrganisationToGroup = (organisationId: string) => {
    mutateGroupToAddOrganisation(
      { groupId: group?.id ?? '', organisationId },
      {
        onSuccess: () => {
          refetchData();
          setShowErrorDialog(false);
        },
        onError: errorHandler(setGroupOrganisationError, setShowErrorDialog),
      },
    );
  };

  const onAdd = (organisationId: string) => {
    const orgToAdd = availableOrganisations?.find(
      (org) => org?.id === organisationId,
    );
    setOrganisationToAdd(orgToAdd);
    setGroupOrganisationError('OTHER');
    const dataProtectionDifferent = getDataProtectionDifference(
      group?.dataRetentionPolicy?.maximumInactivityPeriod,
      orgToAdd?.dataRetentionPolicy?.maximumInactivityPeriod,
    );
    if (
      [
        DataProtectionDifference.GROUP_LONGER,
        DataProtectionDifference.GROUP_SHORTER,
      ].includes(dataProtectionDifferent)
    ) {
      setOpenDataRetentionConfirmationModal(true);
      return;
    }
    addOrganisationToGroup(organisationId);
  };
  return (
    <>
      <Helmet>
        <title>{group?.name ?? ''}</title>
      </Helmet>
      <FullHeight>
        <Container
          maxWidth={false}
          data-testid="group-type-header"
          sx={(theme) => ({
            maxWidth: '100%',
            backgroundColor:
              group?.type === 'PARTNER'
                ? theme.palette.green.light
                : theme.palette.orange.light,
            padding: '0 !important',
          })}
        >
          <Container
            maxWidth={false}
            sx={(theme) => ({
              padding: `${theme.spacing(spacingSizeMap.M)} 0 !important`,
              [theme.breakpoints.up('lg')]: {
                padding: `${theme.spacing(spacingSizeMap.M)}`,
              },
            })}
          >
            <Container maxWidth="lg">
              <Box
                sx={{
                  py: (theme) => theme.spacing(spacingSizeMap.M),
                }}
              >
                <Breadcrumbs
                  crumbs={{
                    '/': t('ui.event-management.dashboard.home.title'),
                    '/organisations': t(
                      'ui.event-management.organisations.title',
                    ),
                  }}
                  activeText={breadcrumbText[group?.type ?? 'CUSTOMER']}
                />
              </Box>
              <Grid
                container
                sx={{
                  justifyContent: 'space-between',
                  flexDirection: 'row',
                }}
              >
                <Grid
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    display: 'flex',
                    '& > h1': {
                      maxWidth: '100%',
                      marginBottom: '18px',
                    },
                  }}
                  size={{ xs: 5.7, md: 5.7 }}
                >
                  <TextV2 component="h1" variant="h2">
                    {group?.name ?? ''}
                  </TextV2>
                  <Chip
                    label={groupTypeLabel[group?.type ?? 'CUSTOMER']}
                    color={group?.type === 'PARTNER' ? 'green' : 'orange'}
                    variant="outlined"
                  />
                  {dataRetentionPeriod && (
                    <Box
                      sx={{
                        marginTop: 2,
                      }}
                    >
                      <Trans
                        i18nKey="ui.event-management.group.data-retention-period"
                        values={{
                          years:
                            parseDurationFromIsoString(dataRetentionPeriod)
                              .years,
                        }}
                        components={{
                          strong: (
                            <TextV2
                              component="span"
                              variant="bodyBold"
                              color="textSecondary"
                            />
                          ),
                          light: (
                            <TextV2 component="span" color="textSecondary" />
                          ),
                        }}
                      />
                    </Box>
                  )}
                </Grid>
                <Grid
                  sx={(theme) => ({
                    [theme.breakpoints.up('sm')]: {
                      marginTop: theme.spacing(spacingSizeMap.S),
                    },
                    alignItems: 'baseline',
                    display: 'flex',
                    justifyContent: 'flex-end',
                  })}
                  size={{ xs: 5, md: 'auto' }}
                >
                  <PermissionGuard
                    requiredPermissions={[
                      {
                        action:
                          'Organisation_Group_Create_Organisation_In_Group',
                        scope: 'Global',
                      },
                    ]}
                  >
                    <ButtonV2
                      onClick={() =>
                        navigate(`/groups/${group?.id}/create-organisation`)
                      }
                      variant="contained"
                      color="primary"
                      buttonIcon={<SvgAddRegular />}
                      sx={{
                        alignSelf: 'flex-start',
                        paddingTop: '8px',
                        paddingBottom: '10px',
                        lineHeight: '28px !important',
                      }}
                      buttonText={t(
                        'ui.event-management.organisations.create-organisation',
                      )}
                    />
                  </PermissionGuard>
                  <PermissionGuard
                    requiredPermissions={[
                      { action: 'Organisation_Group_Update', scope: 'Global' },
                    ]}
                  >
                    <ButtonV2
                      variant="outlined"
                      color="primary"
                      onClick={async () => {
                        setIsOpen(!isOpen);
                        await refetchGroups();
                      }}
                      buttonText={t(
                        'ui.event-management.group.add-remove-organisation.button',
                      )}
                      sx={{ marginLeft: '8px', paddingRight: '14px' }}
                    />
                    <AddRemoveModal
                      isOpen={isOpen}
                      onClose={close}
                      filterBy={['name']}
                      clearSearch={() => {
                        setGroupOrganisationError('OTHER');
                      }}
                      isAddLoading={false}
                      translations={translations}
                      onAdd={onAdd}
                      showError={
                        showErrorDialog || groupOrganisationError !== 'OTHER'
                      }
                      onRemove={(organisationId) => {
                        const orgToAdd = organisations?.find(
                          (org) => org?.id === organisationId,
                        );
                        setOrganisationToAdd(orgToAdd);
                        setGroupOrganisationError('OTHER');
                        return removeOrganisationToGroup(
                          {
                            groupId: group?.id ?? '',
                            organisationId,
                          },
                          {
                            onSuccess: () => {
                              refetchData();
                            },
                            onError: errorHandler(
                              setGroupOrganisationError,
                              setShowErrorDialog,
                            ),
                          },
                        );
                      }}
                      availableItems={availableOrganisations}
                      itemsInGroup={group?.organisations ?? []}
                    />
                    <ActionDropdown
                      actionItems={actionItems}
                      onSelect={(path) => {
                        navigate(path);
                      }}
                      actionText={t('ui.event-management.actions.more')}
                    />
                  </PermissionGuard>
                </Grid>
              </Grid>
            </Container>
          </Container>
        </Container>
        {successfulRoleChange && (
          <Container
            sx={(theme) => ({
              marginTop: theme.spacing(spacingSizeMap.M),
            })}
          >
            <Alert severity="info">
              {t(
                'ui.event-management.bulk-set-user-permissions.success-message',
              )}
            </Alert>
          </Container>
        )}
        <Container
          sx={{
            padding: '0 !important',
          }}
        >
          <GroupOrganisationList
            organisations={group?.organisations ?? []}
            group={group}
            user={user}
          />
        </Container>
      </FullHeight>
      <Dialog
        open={openDataRetentionConfirmationModal}
        title={translations.dataRetentionModal.header}
        primaryButtonText={translations.dataRetentionModal.buttonYes}
        secondaryButtonText={translations.dataRetentionModal.buttonNo}
        dialogProps={{
          sx: {
            '.MuiPaper-root': {
              maxHeight: 'unset',
            },
          },
        }}
        content={
          <>
            <TextV2 component="p">
              <Trans
                i18nKey={translations.dataRetentionModal.body}
                components={{
                  stress: <TextV2 component="span" variant="bodyBold" />,
                }}
              />
            </TextV2>
            <TextV2 component="p" sx={{ marginBlock: '1rem' }}>
              {translations.dataRetentionModal.areYouSure}
            </TextV2>
            <Alert severity="error">
              {translations.dataRetentionModal.alert}
            </Alert>
          </>
        }
        onSubmit={() => {
          setOpenDataRetentionConfirmationModal(false);
          addOrganisationToGroup(organisationToAdd?.id || '');
        }}
        onClose={() => setOpenDataRetentionConfirmationModal(false)}
      />
    </>
  );
};

export default Group;
